import { getCookie } from "@workspace/cookies/browser";
import { mutate } from "swr";
import type { GraphqlResult } from "./GraphqlResult";

/**
 * SWR holds the query cache (one entry per key, in memory, for the page's
 * lifetime). This module decides what a key is and how entries are retired:
 *
 *   [getter, variables, session token, version]
 *
 * - The session token (the `token` cookie) makes a login or logout start
 *   from empty, with no code in the auth flows.
 * - The version is a counter per operation plus a global one. Invalidating
 *   bumps it, so every mounted hook of the operation re-keys and refetches
 *   while keeping its previous data on screen, and entries of the old version
 *   are never read again. Nothing is deleted or expired otherwise.
 */

type Getter = (variables: never) => Promise<GraphqlResult<unknown>>;

let generation = 0;
const versions = new Map<Getter, number>();
const listeners = new Set<() => void>();

const notify = () => {
  for (const listener of listeners) {
    listener();
  }
};

/** For `useSyncExternalStore`: re-renders hooks when a version changes. */
export const subscribeVersions = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getVersion = (getter: Getter) =>
  `${generation}.${versions.get(getter) ?? 0}`;

export const getSessionToken = () => getCookie("token");

export const queryKey = <Variables>(
  getter: (variables: Variables) => Promise<GraphqlResult<unknown>>,
  variables: Variables | undefined,
  extra?: string,
) =>
  [
    getter,
    variables ?? {},
    getSessionToken(),
    `${getVersion(getter as Getter)}${extra ? `#${extra}` : ""}`,
  ] as const;

/**
 * Retires the cached results of an operation and refetches it wherever it is
 * mounted. `useMutation`'s `refetchQueries` calls this after a successful
 * mutation.
 *
 * @example
 * await toggleBonus({ id });
 * invalidateQueries(getMyActiveRewardQueryClient);
 */
export const invalidateQueries = <Variables>(
  getter: (variables: Variables) => Promise<GraphqlResult<unknown>>,
) => {
  versions.set(getter as Getter, (versions.get(getter as Getter) ?? 0) + 1);
  notify();
};

/**
 * Replaces the cached data of one query (current session and version) and
 * re-renders its mounted hooks without a request, e.g. from a subscription
 * payload. A no-op when nothing is cached for those variables.
 */
export const updateQueryCache = <Data, Variables>(
  getter: (variables: Variables) => Promise<GraphqlResult<Data>>,
  variables: Variables,
  update: (data: Data) => Data,
) =>
  mutate<GraphqlResult<Data>>(
    queryKey(getter, variables),
    (result) =>
      result?.data === undefined
        ? result
        : { ...result, data: update(result.data) },
    { revalidate: false },
  );

/** Retires every cached result and refetches every mounted query. */
export const clearQueryCache = () => {
  generation += 1;
  notify();
};
